import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API_BASE_URL } from "../config/api";
import Swal from "sweetalert2";
import { FaSearch, FaCheckCircle, FaTimesCircle, FaEdit, FaTrash, FaUserPlus } from "react-icons/fa";

function ManageTeam() {
  const [staffList, setStaffList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchName, setSearchName] = useState("");

  // System mein available sari permissions yahan define karein
  const availablePermissions = ["Dashboard", "Manage Team", "Inventory", "Sales", "Reports", "Settings"];

  const fetchStaff = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/user/team-list`);
      setStaffList(res.data);
    } catch (err) {
      console.error("Error fetching staff:", err);
      Swal.fire("Error", "Could not load staff list.", "error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStaff();
  }, [fetchStaff]);

  // ✅ TOGGLE STATUS
  const toggleStatus = async (id, currentStatus) => {
    const newStatus = currentStatus === "Active" ? "Inactive" : "Active";
    try {
      const res = await axios.put(`${API_BASE_URL}/user/update-status/${id}`, { status: newStatus });
      if (res.status === 200) {
        setStaffList(prev => prev.map(item => item._id === id ? { ...item, status: newStatus } : item));
        const Toast = Swal.mixin({
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 1500,
        });
        Toast.fire({ icon: 'success', title: `Status: ${newStatus}` });
      }
    } catch (err) {
      Swal.fire("Error", "Could not update status.", "error");
    }
  };

  // ✏️ EDIT STAFF (MODAL WITH PERMISSIONS)
  const editStaff = async (staff) => {
    const { value: formValues } = await Swal.fire({
      title: `<h4 class="fw-bold mb-0">Edit Staff: ${staff.fullName}</h4>`,
      html: `
        <div class="text-start px-2 mt-3" style="max-height: 400px; overflow-y: auto;">
          <div class="mb-3">
            <label class="form-label small fw-bold">Full Name</label>
            <input id="swal-name" class="form-control" value="${staff.fullName}">
          </div>
          <div class="mb-3">
            <label class="form-label small fw-bold">Username</label>
            <input id="swal-username" class="form-control" value="${staff.username}">
          </div>
          <div class="mb-3">
            <label class="form-label small fw-bold">Email</label>
            <input id="swal-email" type="email" class="form-control" value="${staff.email}">
          </div>
          
          <label class="form-label small fw-bold mb-2">Permissions</label>
          <div class="row g-2 p-2 bg-light rounded border">
            ${availablePermissions.map(perm => `
              <div class="col-6">
                <div class="form-check">
                  <input class="form-check-input swal-perm-checkbox" type="checkbox" 
                    value="${perm}" id="perm-${perm}" 
                    ${staff.permissions?.includes(perm) ? 'checked' : ''}>
                  <label class="form-check-label small" for="perm-${perm}">${perm}</label>
                </div>
              </div>
            `).join('')}
          </div>
        </div>
      `,
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: 'Save Changes',
      confirmButtonColor: '#097969',
      preConfirm: () => {
        const fullName = document.getElementById('swal-name').value;
        const username = document.getElementById('swal-username').value;
        const email = document.getElementById('swal-email').value;
        
        // Collect checked permissions
        const checkboxes = document.querySelectorAll('.swal-perm-checkbox:checked');
        const selectedPermissions = Array.from(checkboxes).map(cb => cb.value);

        if (!fullName || !username || !email) {
          Swal.showValidationMessage('Basic details are required');
          return false;
        }
        return { fullName, username, email, permissions: selectedPermissions };
      }
    });

    if (formValues) {
      try {
        const res = await axios.put(`${API_BASE_URL}/user/update-staff/${staff._id}`, formValues);
        if (res.status === 200) {
          setStaffList(prev => prev.map(s => s._id === staff._id ? { ...s, ...formValues } : s));
          Swal.fire({ icon: 'success', title: 'Updated!', timer: 1500, showConfirmButton: false });
        }
      } catch (err) {
        Swal.fire("Error", "Update failed.", "error");
      }
    }
  };

  // 🗑️ DELETE STAFF
  const deleteStaff = async (id) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!'
    });

    if (result.isConfirmed) {
      try {
        await axios.delete(`${API_BASE_URL}/user/delete-staff/${id}`);
        setStaffList(staffList.filter(staff => staff._id !== id));
        Swal.fire('Deleted!', 'Staff member removed.', 'success');
      } catch (err) {
        Swal.fire("Error", "Failed to delete staff.", "error");
      }
    }
  };

  const filteredStaff = staffList.filter((staff) =>
    staff.fullName?.toLowerCase().includes(searchName.toLowerCase()) ||
    staff.username?.toLowerCase().includes(searchName.toLowerCase())
  );

  return (
    <div className="container mt-4 pb-5">
      {/* HEADER */}
      <div className="d-flex justify-content-between align-items-center mb-4 p-4 bg-white shadow-sm rounded-4 border-start border-success border-5">
        <div>
          <h3 className="fw-bold text-dark mb-1">Staff Management</h3>
          <p className="text-muted small mb-0">Control team permissions & status</p>
        </div>
        <div className="text-end d-flex align-items-center gap-3">
          <div className="pe-3 border-end d-none d-sm-block">
            <h2 className="fw-bold mb-0 text-success">{staffList.length}</h2>
            <span className="text-muted small text-uppercase fw-semibold">Total Staff</span>
          </div>
              </div>
      </div>

      {/* SEARCH */}
      <div className="mb-4">
        <div className="position-relative" style={{ maxWidth: "400px" }}>
          <FaSearch className="position-absolute top-50 translate-middle-y ms-3 text-muted" />
          <input
            type="text"
            className="form-control ps-5 border-0 shadow-sm py-2 rounded-3"
            placeholder="Search staff..."
            value={searchName}
            onChange={(e) => setSearchName(e.target.value)}
          />
        </div>
      </div>

      {/* TABLE */}
      <div className="table-responsive shadow-sm rounded-4 border bg-white">
        <table className="table table-hover mb-0">
          <thead className="bg-light">
            <tr className="small text-uppercase text-muted">
              <th className="py-3 px-4 border-0">Member</th>
              <th className="py-3 border-0">Username</th>
              <th className="py-3 border-0 text-center">Status</th>
              <th className="py-3 border-0 text-center">Permissions</th>
              <th className="py-3 border-0 text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="5" className="text-center py-5"><div className="spinner-border text-success"></div></td></tr>
            ) : filteredStaff.map((staff) => (
              <tr key={staff._id} className="align-middle">
                <td className="py-3 px-4">
                  <div className="fw-bold">{staff.fullName}</div>
                  <small className="text-muted">{staff.email}</small>
                </td>
                <td className="text-muted">@{staff.username}</td>
                <td className="text-center">
                  <span 
                    onClick={() => toggleStatus(staff._id, staff.status)}
                    className={`badge px-3 py-2 cursor-pointer rounded-pill`}
                    style={{ 
                      backgroundColor: staff.status === "Inactive" ? "#fee2e2" : "#dcfce7", 
                      color: staff.status === "Inactive" ? "#dc3545" : "#15803d",
                      cursor: "pointer"
                    }}
                  >
                    {staff.status === "Inactive" ? "INACTIVE" : "ACTIVE"}
                  </span>
                </td>
                <td className="text-center">
                  <div className="d-flex flex-wrap justify-content-center gap-1">
                    {staff.permissions?.map((p, i) => (
                      <span key={i} className="badge bg-light text-dark border" style={{ fontSize: "13px" }}>{p}</span>
                    ))}
                  </div>
                </td>
                <td className="text-center">
                  <div className="d-flex justify-content-center gap-2">
                    <button onClick={() => editStaff(staff)} className="btn btn-sm btn-light border text-primary shadow-sm"><FaEdit /></button>
                    <button onClick={() => deleteStaff(staff._id)} className="btn btn-sm btn-light border text-danger shadow-sm"><FaTrash /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default ManageTeam;